import React, { useCallback, useMemo } from 'react';
import moment from 'moment';

import IconButton from '@material-ui/core/IconButton';
import ArrowIcon from '@material-ui/icons/KeyboardArrowLeft';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';

import DeleteIcon from '../../assets/icons/DeleteIcon';
import EditIcon from '../../assets/icons/EditIcon';
import DateIcon from '../../assets/icons/CalendarIcon';
import MoneyBagIcon from '../../assets/icons/MoneyBagIcon';
import UserCheckIcon from '../../assets/icons/UserCheckIcon';
import PhoneIcon from '../../assets/icons/PhoneIcon';
import MailIcon from '../../assets/icons/MailIcon';
import QualificationIcon from '../../assets/icons/QualificationIcon';
import LaunchIcon from '../../assets/icons/LaunchIcon';
import FilterIcon from '../../assets/icons/FilterIcon';
import FilterOffIcon from '../../assets/icons/FilterOffIcon';
import StarCircleIcon from '../../assets/icons/StarCircleIcon';
import CheckCircleIcon from '../../assets/icons/CheckCircleIcon';
import DeleteCircleIcon from '../../assets/icons/DeleteCircleIcon';
import DecreaseIcon from '../../assets/icons/DecreaseIcon';
import IncreaseIcon from '../../assets/icons/IncreaseIcon';
import BookedIcon from '../../assets/icons/BookedIcon';
import LeadActivityIcon from '../../assets/icons/LeadActivityIcon';
import CalendarJumpIcon from '../../assets/icons/CalendarJumpIcon';

import Button from '../shared/Button';
import ActionIconButton from '../shared/ActionIconButton';

import { getLighterColor } from '../../utils/colors';
import { formatPrice } from '../../utils/utils';
import { getDiffDays } from '../../utils/date';
import { getCommaSeparatedNames } from '../../utils/table';

import { defaultRankingAndActivity, leadActivityCategories, leadStatuses } from '../../constants/sales';

import './leads.scss';

const useStyles = makeStyles(() => ({
  tooltip: {
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: 8
  }
}));

const LeadInfo = (props) => {
  const {
    lead,
    onBackClick,
    onDeleteClick,
    onEditClick,
    onSurgeonClick,
    onLostClick,
    onWonClick,
    onReactivateClick,
    labels = [],
    users = [],
    surgeons = [],
    specialities = [],
    pipelines = [],
    stages = [],
    qualifications = [],
    activity = [],
    cases = [],
    isArchived,
    surgeonRanking,
  } = props;

  const classes = useStyles();

  const getLeadTitle = useCallback((lead) => {
    switch (lead.status) {
      case leadStatuses.ACTIVE.value:
        return 'Lead';
      case leadStatuses.WON.value:
        return 'Active Surgeon';
      case leadStatuses.LOST.value:
        return 'Lost Lead';
      default:
        return 'Lead';
    }
  }, []);

  const getLeadIcon = useCallback((lead) => {
    switch (lead.status) {
      case leadStatuses.ACTIVE.value:
        return <FilterIcon color="var(--app-text)" />;
      case leadStatuses.WON.value:
        return <StarCircleIcon color="var(--app-text)" />;
      case leadStatuses.LOST.value:
        return <FilterOffIcon color="var(--app-text)" />;
      default:
        return <FilterIcon color="var(--app-text)" />;
    }
  }, []);

  const getUserName = useCallback((uid) => {
    const user = users.find((u) => u.uid === uid);
    return user ? `${user.firstName} ${user.lastName}` : '';
  }, [users]);

  const getLabelName = useCallback((labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label?.name || '';
  }, [labels]);

  const getLabelColor = useCallback((labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label?.color || '#000000';
  }, [labels]);

  const getSurgeonName = useCallback((surgeonId) => {
    const surgeon = surgeons.find((surgeon) => surgeon.id === surgeonId);
    return surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : '';
  }, [surgeons]);

  const getPipelineTitle = useCallback((pipelineId) => {
    const pipeline = pipelines.find((p) => p.id === pipelineId);
    return pipeline?.title || '';
  }, [pipelines]);

  const getStageTitle = useCallback((stageId) => {
    const stage = stages.find((s) => s.id === stageId);
    return stage?.title || '';
  }, [stages]);

  const getQualificationNames = useCallback((ids) => getCommaSeparatedNames(qualifications, ids) || '---', [surgeons, qualifications]);

  const leadLabels = useMemo(() => lead?.labels?.filter((labelId) => labels.find((l) => l.id === labelId)), [lead, labels]);

  const surgeon = useMemo(() => surgeons.find((surgeon) => surgeon.id === lead.surgeon), [lead, surgeons]);

  const surgeonSpeciality = useMemo(() => {
    if (!surgeon || !lead?.surgeonDetails?.speciality || !specialities?.length) {
      return '';
    }
    const _speciality = specialities?.find((s) => s.id ===  lead?.surgeonDetails?.speciality);
    return _speciality?.name || '';
  }, [lead, surgeon, specialities]);

  const rankingScore = useMemo(() => {
    if (!lead) {
      return '';
    }

    const { ranking } = lead;

    if (!ranking) {
      return '';
    }

    const { teaching, kolStatus, published, training } = ranking;
    const sum = teaching + kolStatus + published + training;
    const _ranking = surgeonRanking || defaultRankingAndActivity;

    if (sum && sum <= 20 && _ranking) {
      return _ranking[sum].rankCategory || 'E';
    }

    return '';
  }, [lead, surgeonRanking]);

  const numberOfActivity = useMemo(() => {
    if (!lead) {
      return null;
    }

    const dateStart = moment().subtract(1, 'month').startOf('month');
    const dateEnd = moment().subtract(1, 'month').endOf('month');

    const bookingActivity = activity?.filter((a) => [leadActivityCategories.TRAINING.value, leadActivityCategories.CALL.value, leadActivityCategories.MEETING.value, leadActivityCategories.DEMO.value].includes(a?.category));
    const stageActivity = activity?.filter((a) => a.type === 'LEAD_STAGE_UPDATED' && dateStart.isBefore(a.createdAt) && dateEnd.isAfter(a.createdAt))
    const lastMonthActivity = bookingActivity?.filter((a) => dateStart.isBefore(a.bookingData?.date) && dateEnd.isAfter(a.bookingData?.date));
    const activityNumber = stageActivity?.length + lastMonthActivity?.length;

    return activityNumber;
  }, [lead, activity, surgeonRanking]);


  const getActivityIcon = useCallback(() => {
    if (!lead) {
      return null;
    }

    const { ranking } = lead;

    if (!ranking) {
      return null;
    }

    const { teaching, kolStatus, published, training } = ranking;
    const sum = teaching + kolStatus + published + training;
    const _ranking = surgeonRanking || defaultRankingAndActivity;
    const score = _ranking[sum];
    const activityTarget = score?.activityTarget || 0;

    if (numberOfActivity >= activityTarget) {
      return <div className='lead-info__activity-icon'><CheckCircleIcon color='#81B214' /></div>;
    } else {
      return <div className='lead-info__activity-icon'><DeleteCircleIcon color='#F53434' /></div>;
    }
  }, [lead, numberOfActivity, surgeonRanking]);

  const numberOfCasesLastMonth = useMemo(() => {
    if (!cases || !lead || lead?.status !== leadStatuses.WON.value) {
      return 0;
    }

    const dateStart = moment().subtract(1, 'month').startOf('month');
    const dateEnd = moment().subtract(1, 'month').endOf('month');

    return cases?.filter((caseItem) => {
      return caseItem.surgeon === lead.surgeon && dateStart.isBefore(caseItem.date) && dateEnd.isAfter(caseItem.date);
    })?.length || 0;
  }, [lead, cases]);

  const numberOfCasesThisMonth = useMemo(() => {
    if (!cases || !lead || lead?.status !== leadStatuses.WON.value) {
      return 0;
    }

    const dateStart = moment();
    const dateEnd = moment().endOf('month');

    return cases?.filter((caseItem) => {
      return caseItem.surgeon === lead.surgeon && dateStart.isBefore(caseItem.date) && dateEnd.isAfter(caseItem.date);
    })?.length || 0;
  }, [lead, cases]);

  const numberOfFutureCases = useMemo(() => {
    if (!cases || !lead || lead?.status !== leadStatuses.WON.value) {
      return 0;
    }

    return cases?.filter((caseItem) => {
      return caseItem.surgeon === lead.surgeon && moment().isBefore(caseItem.date);
    })?.length || 0;
  }, [lead, cases]);

  const caseVolumeMin = useMemo(() => {
    if (!lead || lead?.status !== leadStatuses.WON.value) {
      return 0;
    }
    const _stage = stages?.find((s) => s.id === lead?.stage);
    return _stage?.caseVolumeMin || 0;
  }, [lead, stages]);

  const caseVolumeMax = useMemo(() => {
    if (!lead || lead?.status !== leadStatuses.WON.value) {
      return 0;
    }
    const _stage = stages?.find((s) => s.id === lead?.stage);
    return _stage?.caseVolumeMax || 0;
  }, [lead, stages]);

  const getIcon = useCallback(() => {
    if (numberOfCasesLastMonth < caseVolumeMin) {
      return <div className='lead-info__activity-icon'><DecreaseIcon color='#F53434' fontSize='small' /></div>;
    }
    if (numberOfCasesLastMonth > caseVolumeMax) {
      return <div className='lead-info__activity-icon'><IncreaseIcon color='#81B214' fontSize='small' /></div>;
    }
    return null;
  }, [numberOfCasesLastMonth, caseVolumeMin, caseVolumeMax]);

  return (
    <div className='lead-info__container'>
      <div className='lead-info__actions'>
        <div className='d-flex width-100'>
          <IconButton
            className="m-r-md"
            onClick={onBackClick}
            size="small"
          >
            <ArrowIcon fontSize="large" style={{ color: 'var(--app-text)' }} />
          </IconButton>
          <div className="d-flex flex-1">
            <Tooltip
              title="Archive Lead"
              aria-label="archive-lead"
              classes={{ tooltip: classes.tooltip }}
            >
              <div className="m-l-md">
                <Button
                  type="icon"
                  aria-label="archive-lead"
                  aria-controls="long-menu"
                  aria-haspopup="true"
                  onClick={onDeleteClick}
                  disabled={isArchived}
                >
                  <DeleteIcon color="var(--app-text)" />
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title="Edit Lead"
              aria-label="edit-lead"
              classes={{ tooltip: classes.tooltip }}
            >
              <div className="m-l-md">
                <Button
                  type="icon"
                  onClick={onEditClick}
                  disabled={isArchived || lead.status === leadStatuses.LOST.value}
                >
                  <EditIcon color="var(--app-text)" />
                </Button>
              </div>
            </Tooltip>
          </div>
          {(!lead.status || lead.status === leadStatuses.ACTIVE.value) && (
            <div className='d-flex lead-info__action-buttons'>
              <div>
                <ActionIconButton
                  className='lead-info__won-button'
                  color='green'
                  onClick={onWonClick}
                >
                  Won
                </ActionIconButton>
              </div>
              <div className='m-l-md'>
                <ActionIconButton
                  className='lead-info__lost-button'
                  color='red'
                  onClick={onLostClick}
                >
                  Lost
                </ActionIconButton>
              </div>
            </div>
          )}
          {lead.status === leadStatuses.LOST.value && (
            <div className='d-flex lead-info__action-buttons'>
              <ActionIconButton
                className='lead-info__reactivate-button'
                color='blue'
                onClick={onReactivateClick}
              >
                Reactivate
              </ActionIconButton>
            </div>
          )}
        </div>
      </div>

      <div className='lead-info__surgeon-info'>
        <div className='d-flex space-between'>
          <div className='font-bold font-size-lg'>{getSurgeonName(lead.surgeon)}</div>
          {!!rankingScore && (
            <div className='d-flex'>
              <span>Rank</span>
              <span className='lead-info__ranking_score'>{rankingScore}</span>
            </div>
          )}
        </div>
        {surgeonSpeciality && (
          <div className="m-t-sm secondary">
            {surgeonSpeciality}
          </div>
        )}
        <div className='m-t-lg'>
          <div className='d-flex align-start'>
            <PhoneIcon color="var(--app-text)" />
            <div className='m-l-md'>{surgeon?.phone || '---'}</div>
          </div>
          <div className='d-flex align-start m-t-lg'>
            <MailIcon color="var(--app-text)" />
            <div className='m-l-md'>{surgeon?.email || '---'}</div>
          </div>
          <div className='d-flex space-between m-t-lg'>
            <div className='d-flex align-start'>
              <QualificationIcon color="var(--app-text)" />
              <div className='m-l-md'>{getQualificationNames(surgeon?.qualifications)}</div>
            </div>

            <IconButton
              onClick={() => onSurgeonClick(lead.surgeon)}
              size="small"
            >
              <LaunchIcon fontSize="small" color="var(--app-text)" />
            </IconButton>
          </div>
        </div>
      </div>

      <div className={`lead-info__pipeline-info ${lead.status}`}>
        <div className='d-flex space-between'>
          <div className='d-flex'>
            {getLeadIcon(lead)}
            <div className='m-l-md font-size-lg font-bold'>{getLeadTitle(lead)}</div>
          </div>
          <div className='font-size-sm'>
            {lead?.createdAt ? `Added ${moment(lead.createdAt).fromNow()}` : ''}
          </div>
        </div>
        {lead.status !== leadStatuses.WON.value && (
          <div className='m-t-md font-bold'>
            {getPipelineTitle(lead.pipeline)}
          </div>
        )}
        <div className='m-t-sm font-bold'>
          {getStageTitle(lead.stage)}
        </div>
        {!!lead.stageUpdatedAt && (
          <div className='m-t-sm'>
            {`${getDiffDays(moment(), lead.stageUpdatedAt)} day${getDiffDays(moment(), lead.stageUpdatedAt) > 1 ? 's' : ''} in Stage`}
          </div>
        )}
      </div>

      <div className='lead-info__main'>
        {!!leadLabels?.length && (
          <div className='d-flex m-b-md'>
            {leadLabels.map((labelId) => (
              <div
                key={labelId}
                className='lead-info__label'
                style={{
                  '--labelColor': getLabelColor(labelId),
                  '--backgroundColor': getLighterColor(getLabelColor(labelId))
                }}
              >
                {getLabelName(labelId)}
              </div>
            ))}
          </div>
        )}
        {lead?.status !== leadStatuses.WON.value && (
          <div className='font-bold font-size-lg'>{lead.title}</div>
        )}

        <div className='m-t-md'>
          {lead.status !== leadStatuses.WON.value && (
            <div className='d-flex align-start m-t-sm'>
              <DateIcon color="var(--app-text)" />
              <div className='m-l-lg'>{`${moment(lead.date).local().format('MMM Do YYYY')} (expected close)`}</div>
            </div>
          )}
          <div className='d-flex align-start' style={{ marginTop: lead.status === leadStatuses.WON.value && !!leadLabels?.length ? '16px' : '8px' }}>
            <MoneyBagIcon color="var(--app-text)" />
            <div className='m-l-lg'>{`$${formatPrice(lead.anticipatedValue, 0)} (estimated annually)`}</div>
          </div>
          <div className='d-flex align-start m-t-md'>
            <UserCheckIcon color="var(--app-text)" />
            <div className='m-l-lg'>{`Assigned to ${getUserName(lead.owner)}`}</div>
          </div>
          {lead.status === leadStatuses.WON.value && (
            <div className='d-flex align-start m-t-md'>
              <BookedIcon color="var(--app-text)" />
              <div className='m-l-lg d-flex'>
                <span className='font-bold'>{numberOfCasesLastMonth}</span>
                <span className='m-l-sm'>{`Case${numberOfCasesLastMonth !== 1 ? 's' : ''} last month`}</span>
                {getIcon()}
              </div>
            </div>
          )}
          {lead.status === leadStatuses.WON.value && (
            <div className='d-flex align-start m-t-md'>
              <LeadActivityIcon color="var(--app-text)" />
              <div className='m-l-lg d-flex'>
                <span className='font-bold'>{numberOfActivity}</span>
                <span className='m-l-sm'>Activity last month</span>
                {getActivityIcon()}
              </div>
            </div>
          )}
          {lead.status === leadStatuses.WON.value && (
            <div className='d-flex align-start m-t-md'>
              <CalendarJumpIcon color="var(--app-text)" />
              <div className='m-l-lg d-flex'>
                <span className='font-bold'>{numberOfFutureCases}</span>
                <span className='m-l-sm'>Future cases booked</span>
                <span className='m-l-sm'>{`(${numberOfCasesThisMonth} for ${new Date().toLocaleString('en-US', { month: 'long' })})`}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadInfo;
